/**
 * @file CompareView.jsx
 * @description Yan yana ilan karşılaştırma görünümü
 *
 * Seçilen ilanları yan yana grid formatında gösterir.
 * Fiyat, m², oda sayısı ve diğer özellikleri karşılaştırmayı kolaylaştırır.
 *
 * @module widget/components/CompareView
 */

import { X, MapPin, Home, Ruler, Calendar, Building2 } from "lucide-react";
import { Image } from "@openai/apps-sdk-ui/components/Image";
import { formatPrice, toEmlakjetUrl } from "../utils";
import { openExternal } from "../utils";

/**
 * Karşılaştırma Görünüm Bileşeni
 *
 * @component
 * @param {Object} props
 * @param {Array} props.listings - Karşılaştırılacak ilanlar (2-3)
 * @param {boolean} props.isDark - Karanlık mod
 * @param {Function} props.onClose - Kapatma callback'i
 * @param {Function} props.onRemove - İlan kaldırma callback'i
 */
export function CompareView({ listings, isDark, onClose, onRemove }) {
  if (!listings || listings.length < 2) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-5xl max-h-[90vh] mx-4 rounded-2xl shadow-2xl overflow-hidden flex flex-col ${
          isDark ? "bg-zinc-900" : "bg-white"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b flex-shrink-0 ${
            isDark ? "border-white/10" : "border-black/10"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            İlan Karşılaştırma ({listings.length})
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDark
                ? "hover:bg-white/10 text-white/60"
                : "hover:bg-gray-100 text-gray-500"
            }`}
            aria-label="Kapat"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-auto p-6">
          <div
            className={`grid gap-4`}
            style={{
              gridTemplateColumns: `repeat(${listings.length}, minmax(200px, 1fr))`,
            }}
          >
            {listings.map((listing) => (
              <CompareCard
                key={listing.id}
                listing={listing}
                isDark={isDark}
                onRemove={() => onRemove(listing.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Karşılaştırma Kartı Bileşeni
 */
function CompareCard({ listing, isDark, onRemove }) {
  const priceLabel = formatPrice(listing?.price, listing?.currency);
  const imageUrl = listing?.images?.[0] || listing?.image;
  const href = toEmlakjetUrl(listing?.url);

  // Quick info items
  const infoItems = [
    { icon: Home, label: "Oda", value: listing?.room },
    { icon: Ruler, label: "m²", value: listing?.m2 ? `${listing.m2} m²` : null },
    { icon: Building2, label: "Kat", value: listing?.floor },
    { icon: Calendar, label: "Güncelleme", value: listing?.updatedAt ? new Date(listing.updatedAt).toLocaleDateString("tr-TR") : null },
  ].filter((item) => item.value);

  return (
    <div
      className={`rounded-xl border overflow-hidden flex flex-col ${
        isDark ? "border-white/10 bg-zinc-800" : "border-gray-200 bg-gray-50"
      }`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3]">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={listing?.title || "İlan"}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className={`w-full h-full flex items-center justify-center ${
              isDark ? "bg-white/5" : "bg-gray-200"
            }`}
          >
            <span className={`text-sm ${isDark ? "text-white/30" : "text-gray-400"}`}>
              Görsel yok
            </span>
          </div>
        )}

        {/* Remove button */}
        <button
          onClick={onRemove}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          aria-label="Karşılaştırmadan kaldır"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 flex-1 flex flex-col gap-3">
        {/* Title */}
        <h3
          className={`font-semibold text-sm line-clamp-2 ${
            isDark ? "text-white" : "text-gray-900"
          }`}
        >
          {listing?.title || "İlan"}
        </h3>

        {/* Location */}
        {listing?.location && (
          <div
            className={`flex items-center gap-1.5 text-xs ${
              isDark ? "text-white/50" : "text-gray-500"
            }`}
          >
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} />
            <span className="truncate">{listing.location}</span>
          </div>
        )}

        {/* Price - Highlighted */}
        <div className="text-xl font-bold text-emerald-600">
          {priceLabel || "—"}
        </div>

        {/* Info Grid */}
        <div
          className={`grid grid-cols-2 gap-2 pt-3 border-t ${
            isDark ? "border-white/10" : "border-gray-200"
          }`}
        >
          {infoItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                className={`flex items-center gap-2 text-xs ${
                  isDark ? "text-white/60" : "text-gray-600"
                }`}
              >
                <Icon className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} />
                <span className="truncate">{item.value}</span>
              </div>
            );
          })}
        </div>

        {/* View on Emlakjet button */}
        {href && (
          <button
            onClick={() => openExternal(href)}
            className="mt-auto w-full py-2 text-xs font-medium text-center rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          >
            Emlakjet'te Gör
          </button>
        )}
      </div>
    </div>
  );
}

export default CompareView;
